import { IS_SERVER, RUNTIME } from "@/constants";
import { ErrorOriginEnum, LoggerLevelEnum, RuntimeEnum } from "@/enums";
import { isAppError, isSupabaseErrorLike } from "@/errors";
import type { NormalizedError, UnknownRecord } from "@/types";

function readStringProp(obj: unknown, key: string): string | undefined {
  if (obj && typeof obj === "object" && key in obj) {
    const val = (obj as UnknownRecord)[key];
    return typeof val === "string" ? val : undefined;
  }
  return undefined;
}

function readNumberProp(obj: unknown, key: string): number | undefined {
  if (obj && typeof obj === "object" && key in obj) {
    const raw = (obj as UnknownRecord)[key];
    const val = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(val) && val > 0 ? val : undefined;
  }
  return undefined;
}

function readObjectProp(
  obj: unknown,
  key: string,
): Record<string, unknown> | undefined {
  if (obj && typeof obj === "object" && key in obj) {
    const val = (obj as UnknownRecord)[key];
    return val && typeof val === "object" ? (val as UnknownRecord) : undefined;
  }
  return undefined;
}

function trimStack(err: unknown, max = 3000): string | undefined {
  const s = readStringProp(err, "stack");
  return s ? s.slice(0, max) : undefined;
}

function mapCause(err: unknown): NormalizedError["cause"] | undefined {
  const name = readStringProp(err, "name");
  const message = readStringProp(err, "message");
  const code = readStringProp(err, "code");
  return name || message || code ? { name, message, code } : undefined;
}

const DEFAULTS = {
  name: "Error",
  code: "UNKNOWN",
  message: "Unexpected error",
};

export function normalizeError(e: unknown): NormalizedError {
  const serverRuntime =
    RUNTIME === RuntimeEnum.EDGE ? RuntimeEnum.EDGE : RuntimeEnum.SERVER;
  const digest = readStringProp(e, "digest");
  const runtime = digest
    ? serverRuntime
    : IS_SERVER
      ? serverRuntime
      : RuntimeEnum.CLIENT;

  // AppError first
  if (isAppError(e))
    return {
      name: e.name,
      digest,
      code: e.code ?? DEFAULTS.code,
      message: e.message ?? DEFAULTS.message,
      severity: e.severity,
      origin: ErrorOriginEnum.APP,
      runtime,
      status: e.status,
      details: e.meta,
      stack: trimStack(e),
      cause: mapCause((e as unknown as { cause?: unknown })?.cause),
    };

  // Supabase/PostgREST
  if (isSupabaseErrorLike(e)) {
    const status = readNumberProp(e, "status");
    const details = readObjectProp(e, "details");
    const code = readStringProp(e, "code") ?? DEFAULTS.code;
    const message = readStringProp(e, "message") ?? DEFAULTS.message;
    const name = readStringProp(e, "name") ?? "SupabaseError";
    const severity =
      name === "PostgrestError"
        ? LoggerLevelEnum.ERROR
        : status && status >= 500
          ? LoggerLevelEnum.ERROR
          : LoggerLevelEnum.WARN;
    return {
      name,
      digest,
      code,
      message,
      severity,
      origin: ErrorOriginEnum.SUPABASE,
      runtime,
      status,
      details,
      stack: trimStack(e),
    };
  }

  // HTTP Response
  if (typeof Response !== "undefined" && e instanceof Response) {
    const code = `HTTP_${e.status}`;
    return {
      name: "HttpResponse",
      digest,
      code,
      message: e.statusText || code,
      severity: e.status >= 500 ? LoggerLevelEnum.ERROR : LoggerLevelEnum.WARN,
      origin: ErrorOriginEnum.HTTP,
      runtime,
      status: e.status,
    };
  }

  // Native Error
  if (e instanceof Error) {
    return {
      name: e.name || DEFAULTS.name,
      digest,
      code: "ERROR",
      message: e.message || DEFAULTS.message,
      severity: LoggerLevelEnum.ERROR,
      origin: ErrorOriginEnum.JS,
      runtime,
      stack: trimStack(e),
      cause: mapCause((e as unknown as { cause?: unknown })?.cause),
    };
  }

  // Unknown
  return {
    name: DEFAULTS.name,
    digest,
    code: DEFAULTS.code,
    message: DEFAULTS.message,
    severity: LoggerLevelEnum.ERROR,
    origin: ErrorOriginEnum.UNKNOWN,
    runtime,
  };
}
